import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Calculator,
  Shield,
  TrendingUp,
  Database,
  Activity,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info,
  Eye,
  Zap,
  Scale,
  Target,
  Bell,
  Users,
} from 'lucide-react';
import { cn, calculatePBNScore, calculateLinkHealth } from '../lib/utils';
import { recalculateQualityScores } from '../api/dataforseo';

function FormulaSection({ icon: Icon, iconColor, title, subtitle, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="glow-card-filled overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', iconColor)}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {isOpen ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-0 border-t border-border/30">
          {children}
        </div>
      )}
    </div>
  );
}

function WeightBar({ label, weight, color = 'bg-primary', desc }) {
  const pct = Math.round(weight * 100);
  return (
    <div className="group">
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground w-44 shrink-0">{label}</span>
        <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all flex items-center justify-end pr-1.5', color)}
            style={{ width: `${Math.max(pct, 8)}%` }}
          >
            {pct >= 10 && <span className="text-[10px] font-bold text-white">{pct}%</span>}
          </div>
        </div>
        {pct < 10 && <span className="text-sm font-mono text-foreground w-10 text-right">{pct}%</span>}
      </div>
      {desc && (
        <p className="text-[11px] text-muted-foreground/70 ml-[11.5rem] mt-0.5 hidden group-hover:block">{desc}</p>
      )}
    </div>
  );
}

function GradeChip({ label, color, bg, range }) {
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg border border-border/30 bg-card/50 min-w-[140px]">
      <span className={cn('px-2.5 py-1 rounded-full text-xs font-semibold', bg, color)}>
        {label}
      </span>
      <span className="text-xs text-muted-foreground font-mono">{range}</span>
    </div>
  );
}

function PenaltyBar({ pbn, penalty, label }) {
  const reduction = Math.round((1 - penalty) * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-muted-foreground w-14 shrink-0 text-center">{label}</span>
      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden flex">
        <div
          className="h-full bg-success/60 rounded-l-full transition-all"
          style={{ width: `${penalty * 100}%` }}
        />
        {reduction > 0 && (
          <div
            className="h-full bg-destructive/40 rounded-r-full transition-all"
            style={{ width: `${reduction}%` }}
          />
        )}
      </div>
      <span className={cn(
        'text-xs font-mono w-12 text-right shrink-0',
        reduction === 0 ? 'text-success' : reduction <= 20 ? 'text-warning' : 'text-destructive'
      )}>
        {reduction === 0 ? '×1.0' : `×${penalty.toFixed(2)}`}
      </span>
    </div>
  );
}

function RatioRow({ range, points, severity, example }) {
  return (
    <div className={cn(
      'flex items-center gap-3 p-3 rounded-lg border',
      severity === 'normal' ? 'bg-success/5 border-success/20' :
      severity === 'elevated' ? 'bg-warning/5 border-warning/20' :
      severity === 'high' ? 'bg-orange-400/5 border-orange-400/20' :
      'bg-destructive/5 border-destructive/20'
    )}>
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{range}</p>
        {example && <p className="text-[11px] text-muted-foreground mt-0.5">{example}</p>}
      </div>
      <span className={cn(
        'text-xs font-semibold px-2 py-0.5 rounded-full',
        severity === 'normal' ? 'bg-success/15 text-success' :
        severity === 'elevated' ? 'bg-warning/15 text-warning' :
        severity === 'high' ? 'bg-orange-400/15 text-orange-400' :
        'bg-destructive/15 text-destructive'
      )}>
        {points}
      </span>
    </div>
  );
}

// Interactive PBN calculator
function PBNCalculator() {
  const [da, setDa] = useState(40);
  const [traffic, setTraffic] = useState(500);
  const [backlinks, setBacklinks] = useState(1000);
  const [refDomains, setRefDomains] = useState(50);
  const [refIps, setRefIps] = useState(40);
  const [spamScore, setSpamScore] = useState(3);
  const [dfsSpam, setDfsSpam] = useState(15);

  const result = useMemo(() => calculatePBNScore({
    da, traffic, referringDomains: refDomains, referringIps: refIps,
    backlinksCount: backlinks, spamScore, dfsSpamScore: dfsSpam,
  }), [da, traffic, backlinks, refDomains, refIps, spamScore, dfsSpam]);

  const trustColors = {
    trusted: 'text-success bg-success/10 border-success/30',
    caution: 'text-warning bg-warning/10 border-warning/30',
    suspicious: 'text-destructive bg-destructive/10 border-destructive/30',
    unknown: 'text-muted-foreground bg-muted/30 border-border',
  };

  return (
    <div className="p-4 bg-card border border-border rounded-xl space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold text-foreground">Interactive PBN Calculator</h4>
      </div>
      <p className="text-xs text-muted-foreground">Adjust metrics to see how PBN score and trust level change in real-time.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'DA', value: da, set: setDa, min: 0, max: 100 },
          { label: 'Traffic', value: traffic, set: setTraffic, min: 0, max: 50000, step: 100 },
          { label: 'Backlinks', value: backlinks, set: setBacklinks, min: 0, max: 500000, step: 100 },
          { label: 'Ref. Domains', value: refDomains, set: setRefDomains, min: 0, max: 10000 },
          { label: 'Ref. IPs', value: refIps, set: setRefIps, min: 0, max: 10000 },
          { label: 'Moz Spam (0-17)', value: spamScore, set: setSpamScore, min: 0, max: 17 },
          { label: 'DFS Spam (0-100)', value: dfsSpam, set: setDfsSpam, min: 0, max: 100 },
        ].map(({ label, value, set, min, max, step }) => (
          <div key={label}>
            <label className="text-[11px] text-muted-foreground block mb-1">{label}</label>
            <input
              type="number"
              value={value}
              onChange={e => set(Number(e.target.value))}
              min={min}
              max={max}
              step={step || 1}
              className="w-full px-2 py-1.5 text-sm bg-muted border border-border rounded-lg text-foreground font-mono"
            />
          </div>
        ))}
      </div>

      {/* Result */}
      <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 border border-border/50">
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{result.pbnScore}<span className="text-sm text-muted-foreground font-normal">/7</span></p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">PBN Score</p>
        </div>
        <div className="w-px h-10 bg-border" />
        <div>
          <span className={cn('px-3 py-1 rounded-full text-sm font-semibold border', trustColors[result.trustLevel])}>
            {result.trustLevel.charAt(0).toUpperCase() + result.trustLevel.slice(1)}
          </span>
        </div>
        <div className="w-px h-10 bg-border" />
        <div className="flex-1">
          {result.signals.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {result.signals.map((s, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-warning/10 text-warning border border-warning/20">
                  {s}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-success">No suspicious signals detected</span>
          )}
        </div>
      </div>

      {refDomains > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Backlinks/Refs ratio: <span className="font-mono font-medium text-foreground">{Math.round(backlinks / refDomains)}:1</span>
          {refIps > 0 && <> | IP/Refs ratio: <span className="font-mono font-medium text-foreground">{(refIps / refDomains).toFixed(2)}</span></>}
        </p>
      )}
    </div>
  );
}

export default function Formulas() {
  const queryClient = useQueryClient();

  const recalcMutation = useMutation({
    mutationFn: recalculateQualityScores,
    onSuccess: (data) => {
      toast.success(data.message || 'Quality scores recalculated');
      queryClient.invalidateQueries(['backlinks']);
      queryClient.invalidateQueries(['backlink']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Recalculation failed');
    },
  });

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Scoring Formulas</h1>
        <p className="text-muted-foreground mt-1">
          How BacklinkGuard calculates quality scores, trust levels, and health metrics for your backlinks.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <Info className="h-5 w-5 text-primary shrink-0" />
        <p className="text-sm text-foreground flex-1">
          Quality scores are recalculated when Moz or DataForSEO metrics are refreshed. You can also force a recalculation using the latest formula.
        </p>
        <button
          onClick={() => recalcMutation.mutate()}
          disabled={recalcMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors disabled:opacity-50 text-sm whitespace-nowrap"
        >
          {recalcMutation.isPending ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {recalcMutation.isPending ? 'Recalculating...' : 'Recalculate All'}
        </button>
      </div>

      {/* ============================================ */}
      {/* QUALITY SCORE */}
      {/* ============================================ */}
      <FormulaSection
        icon={TrendingUp}
        iconColor="bg-blue-500/10 text-blue-500"
        title="Quality Score (0-100)"
        subtitle="Composite metric rating each backlink's overall value"
        defaultOpen={true}
      >
        <div className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            The Quality Score combines <strong>9 weighted factors</strong> when DataForSEO data is available,
            or <strong>5 factors</strong> with Moz-only data. After calculation, a{' '}
            <strong>PBN penalty</strong> is applied to reduce scores for suspicious domains.
          </p>

          {/* Visual: Score = Base Score x PBN Penalty */}
          <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl text-sm">
            <div className="text-center p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-xs text-muted-foreground">Base Score</p>
              <p className="text-lg font-bold text-blue-500">9 factors</p>
            </div>
            <span className="text-xl text-muted-foreground">&times;</span>
            <div className="text-center p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <p className="text-xs text-muted-foreground">PBN Penalty</p>
              <p className="text-lg font-bold text-purple-500">0.30 - 1.0</p>
            </div>
            <span className="text-xl text-muted-foreground">=</span>
            <div className="text-center p-3 bg-success/10 rounded-lg border border-success/20">
              <p className="text-xs text-muted-foreground">Final Score</p>
              <p className="text-lg font-bold text-success">0 - 100</p>
            </div>
          </div>

          {/* Full Formula */}
          <div>
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Database className="h-4 w-4 text-emerald-500" />
              Full Formula (DFS + Moz Data)
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Used when DataForSEO metrics are available. Positive factors add to the score, spam subtracts from it.
            </p>
            <div className="space-y-2">
              <WeightBar label="DFS Domain Rank" weight={0.20} color="bg-emerald-500" desc="Lower rank = higher score. Sigmoid curve with midpoint 30K." />
              <WeightBar label="Traffic Signal" weight={0.15} color="bg-emerald-500" desc="Organic traffic legitimacy. Sigmoid with midpoint 500 visits/mo." />
              <WeightBar label="Domain Ecosystem" weight={0.15} color="bg-emerald-500" desc="Referring domain diversity. Sigmoid with midpoint 50 domains." />
              <WeightBar label="Stability" weight={0.15} color="bg-blue-500" desc="Monitoring health: uptime x (1 - failure rate)." />
              <WeightBar label="Spam Risk" weight={0.15} color="bg-destructive" desc="Worst of Moz (0-17) and DFS (0-100) spam scores. SUBTRACTED." />
              <WeightBar label="Moz DA" weight={0.05} color="bg-blue-400" desc="Domain Authority (0-100) normalized." />
              <WeightBar label="Moz PA" weight={0.05} color="bg-blue-400" desc="Page Authority (0-100) normalized." />
              <WeightBar label="IP Diversity" weight={0.05} color="bg-purple-500" desc="Unique IPs / referring domains. Higher = more diverse hosting." />
              <WeightBar label="Anchor Diversity" weight={0.05} color="bg-purple-500" desc="Variety of anchor text used. Default 0.5 if unknown." />
            </div>
            <div className="mt-3 p-3 bg-muted/50 rounded-lg font-mono text-xs text-muted-foreground leading-relaxed">
              <span className="text-foreground">Score</span> = 0.20&times;DFSRank + 0.15&times;Traffic + 0.15&times;Ecosystem + 0.15&times;Stability<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-destructive">- 0.15&times;SpamRisk</span> + 0.05&times;DA + 0.05&times;PA + 0.05&times;IPDiv + 0.05&times;AnchorDiv
            </div>
          </div>

          {/* Moz-Only Formula */}
          <div>
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              Moz-Only Formula (No DFS Data)
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              When DataForSEO data hasn't been fetched yet. Scores marked with <strong>*</strong> use this formula.
            </p>
            <div className="space-y-2">
              <WeightBar label="Moz DA" weight={0.25} color="bg-blue-500" />
              <WeightBar label="Stability" weight={0.20} color="bg-blue-500" />
              <WeightBar label="Spam Risk (Moz)" weight={0.20} color="bg-destructive" />
              <WeightBar label="Moz PA" weight={0.15} color="bg-blue-400" />
              <WeightBar label="Anchor Diversity" weight={0.10} color="bg-purple-500" />
            </div>
          </div>

          {/* Grade Thresholds */}
          <div>
            <h3 className="font-medium text-foreground mb-3">Grade Thresholds</h3>
            <div className="flex flex-wrap gap-3">
              <GradeChip label="Strong" color="text-success" bg="bg-success/15" range="60 - 100" />
              <GradeChip label="Moderate" color="text-warning" bg="bg-warning/15" range="35 - 59" />
              <GradeChip label="Weak" color="text-orange-400" bg="bg-orange-400/15" range="15 - 34" />
              <GradeChip label="Toxic" color="text-destructive" bg="bg-destructive/15" range="0 - 14" />
            </div>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* PBN SCORE & TRUST LEVEL */}
      {/* ============================================ */}
      <FormulaSection
        icon={Shield}
        iconColor="bg-purple-500/10 text-purple-500"
        title="PBN Score & Trust Level"
        subtitle="Private Blog Network detection with graduated scoring"
      >
        <div className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            The PBN Score detects signs of link manipulation using <strong>5 signals</strong>.
            The inflated link profile signal uses <strong>graduated scoring</strong> — more extreme
            ratios contribute more points. Maximum score is <strong>7</strong>.
          </p>

          {/* PBN Signals */}
          <div>
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4 text-warning" />
              Detection Signals
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">DA/Traffic Mismatch</p>
                  <p className="text-xs text-muted-foreground">DA &ge; 20 but confirmed traffic &lt; 100 visits/month</p>
                </div>
                <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">+1</span>
              </div>

              {/* Inflated Link Profile - Graduated */}
              <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Inflated Link Profile <span className="text-xs text-primary font-normal">(graduated)</span></p>
                    <p className="text-xs text-muted-foreground mb-2">
                      Backlinks-to-referring-domains ratio measures how many links come from each unique domain.
                      A healthy site has 1-5 backlinks per referring domain.
                    </p>
                  </div>
                </div>
                <div className="space-y-1.5 ml-7">
                  <RatioRow range="1:1 to 10:1" points="Normal" severity="normal" example="e.g., 500 backlinks from 100 domains" />
                  <RatioRow range="10:1 to 50:1" points="+1" severity="elevated" example="e.g., 2,500 backlinks from 50 domains" />
                  <RatioRow range="50:1 to 500:1" points="+2" severity="high" example="e.g., 10,000 backlinks from 100 domains" />
                  <RatioRow range="500:1 and above" points="+3" severity="extreme" example="e.g., 336K backlinks from 45 domains" />
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Shared IP Footprint</p>
                  <p className="text-xs text-muted-foreground">
                    Referring IPs / Referring Domains &lt; 0.3 — many domains on few servers (PBN hosting pattern).
                    Only checked when IP data is available.
                  </p>
                </div>
                <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">+1</span>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Zero Traffic</p>
                  <p className="text-xs text-muted-foreground">
                    Domain has confirmed 0 organic traffic. Missing traffic data is <em>not</em> penalized.
                  </p>
                </div>
                <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">+1</span>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">High Spam Score</p>
                  <p className="text-xs text-muted-foreground">
                    Worst of Moz (&gt;5/17) and DFS (&gt;30/100) spam scores exceeds 30% threshold
                  </p>
                </div>
                <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">+1</span>
              </div>
            </div>
          </div>

          {/* PBN Penalty Applied to Quality Score */}
          <div>
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Scale className="h-4 w-4 text-destructive" />
              PBN Penalty (Applied to Quality Score)
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              The PBN score applies a <strong>multiplier</strong> to the quality score. Higher PBN = lower multiplier.
              For example, PBN 4 applies &times;0.65 (keeps 65% of the base score). The green bar shows what's kept, red shows the reduction.
            </p>
            <div className="space-y-1.5">
              <PenaltyBar pbn={0} penalty={1.0} label="PBN 0-1" />
              <PenaltyBar pbn={2} penalty={0.90} label="PBN 2" />
              <PenaltyBar pbn={3} penalty={0.80} label="PBN 3" />
              <PenaltyBar pbn={4} penalty={0.65} label="PBN 4" />
              <PenaltyBar pbn={5} penalty={0.50} label="PBN 5" />
              <PenaltyBar pbn={6} penalty={0.40} label="PBN 6" />
              <PenaltyBar pbn={7} penalty={0.30} label="PBN 7" />
            </div>
            <div className="mt-3 p-3 bg-muted/30 rounded-lg space-y-1.5">
              <p className="text-xs font-medium text-foreground">Worked Example:</p>
              <p className="text-xs text-muted-foreground">
                A backlink with base quality <span className="font-mono text-foreground">65</span> (Strong) and PBN score <span className="font-mono text-foreground">4</span>:
              </p>
              <p className="text-xs font-mono text-muted-foreground">
                Final = 65 &times; 0.65 = <span className="text-warning font-semibold">42</span> (Moderate)
              </p>
              <p className="text-[11px] text-muted-foreground">
                The &times;0.65 multiplier kept 65% of the score, dropping it from Strong to Moderate.
              </p>
            </div>
          </div>

          {/* Trust Level Thresholds */}
          <div>
            <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              Trust Level Classification
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-success/5 border border-success/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-sm font-medium text-success">Trusted</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-muted-foreground">PBN &le; 1, DA &ge; 20, Traffic &gt; 500, Refs &ge; 10</span>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/30 border border-border/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Unknown</span>
                </div>
                <span className="text-xs text-muted-foreground">PBN &le; 1, doesn't meet Trusted criteria</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-warning/5 border border-warning/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium text-warning">Caution</span>
                </div>
                <span className="text-xs text-muted-foreground">PBN 2 - 3</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium text-destructive">Suspicious</span>
                </div>
                <span className="text-xs text-muted-foreground">PBN &ge; 4</span>
              </div>
            </div>
          </div>

          {/* Interactive Calculator */}
          <PBNCalculator />

          {/* Trust vs Quality Note */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Trust vs Quality — They Measure Different Things</strong>
            </p>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-muted-foreground">
              <div className="p-2 bg-purple-500/5 rounded-lg">
                <p className="font-medium text-purple-400 mb-1">Trust Level</p>
                <p>Detects PBN/spam network patterns. Uses binary signals (inflated ratios, shared IPs, zero traffic).</p>
              </div>
              <div className="p-2 bg-blue-500/5 rounded-lg">
                <p className="font-medium text-blue-400 mb-1">Quality Score</p>
                <p>Evaluates overall backlink value. Weighted formula including authority, stability, diversity, traffic.</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              A site can be "Trusted" (not a PBN) but "Moderate" quality (slow, limited diversity).
              Conversely, a PBN site can appear "Strong" on raw metrics but get penalized by the PBN multiplier.
            </p>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* REFERENCE: HEALTHY VS SUSPICIOUS RATIOS */}
      {/* ============================================ */}
      <FormulaSection
        icon={Zap}
        iconColor="bg-orange-500/10 text-orange-500"
        title="Reference: Healthy vs Suspicious Ratios"
        subtitle="Industry benchmarks for evaluating link profiles"
      >
        <div className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            These thresholds are based on industry standards from Ahrefs, Semrush, Moz, and Majestic research.
          </p>

          {/* Backlinks : Referring Domains */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">Backlinks : Referring Domains Ratio</h3>
            <p className="text-xs text-muted-foreground mb-2">How many backlinks come from each unique domain. Multiple links from one domain is normal (sitewide, multiple pages), but extreme ratios suggest manipulation.</p>
            <div className="space-y-1.5">
              <RatioRow range="1:1 to 5:1" points="Healthy" severity="normal" example="Typical for editorial links, guest posts" />
              <RatioRow range="5:1 to 10:1" points="Normal" severity="normal" example="Sitewide links, footer/sidebar links" />
              <RatioRow range="10:1 to 50:1" points="Elevated" severity="elevated" example="Worth monitoring — could be natural or paid" />
              <RatioRow range="50:1 to 500:1" points="Suspicious" severity="high" example="Likely link scheme or PBN" />
              <RatioRow range="500:1+" points="Extreme" severity="extreme" example="Almost certainly manipulated" />
            </div>
          </div>

          {/* Referring IPs : Referring Domains */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">Referring IPs : Referring Domains Ratio</h3>
            <p className="text-xs text-muted-foreground mb-2">If many domains share few IP addresses, they're likely on the same server — a classic PBN footprint.</p>
            <div className="space-y-1.5">
              <RatioRow range="> 0.7" points="Healthy" severity="normal" example="Good IP diversity — domains on many servers" />
              <RatioRow range="0.3 to 0.7" points="Moderate" severity="elevated" example="Some hosting concentration" />
              <RatioRow range="< 0.3" points="Suspicious" severity="high" example="Many domains, few IPs — shared hosting pattern" />
              <RatioRow range="< 0.1" points="Highly Suspicious" severity="extreme" example="Strong PBN/link farm footprint" />
            </div>
          </div>

          {/* DA vs Traffic */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-2">Domain Authority vs Organic Traffic</h3>
            <p className="text-xs text-muted-foreground mb-2">
              Legitimate high-DA domains have organic traffic from search rankings. A domain with high DA but zero traffic likely has inflated authority.
            </p>
            <div className="space-y-1.5">
              <RatioRow range="DA 30+, Traffic > 1K" points="Healthy" severity="normal" example="Real authority backed by real traffic" />
              <RatioRow range="DA 20+, Traffic 100-500" points="Moderate" severity="elevated" example="New or niche site, worth monitoring" />
              <RatioRow range="DA 20+, Traffic < 100" points="Suspicious" severity="high" example="Authority without visitors — manipulation signal" />
              <RatioRow range="DA 30+, Traffic = 0" points="Highly Suspicious" severity="extreme" example="No rankings despite authority — likely PBN" />
            </div>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* INDIVIDUAL SIGNAL CALCULATIONS */}
      {/* ============================================ */}
      <FormulaSection
        icon={Calculator}
        iconColor="bg-orange-500/10 text-orange-500"
        title="Individual Signal Calculations"
        subtitle="How each factor is computed from raw metrics"
      >
        <div className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">
            Each factor is normalized to 0-1 using sigmoid curves (for smooth, bounded output) or simple ratios.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* DFS Rank */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                DFS Domain Rank
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                signal = 1 - sigmoid(rank, 30000)
              </code>
              <div className="flex gap-3 text-[11px] text-muted-foreground">
                <span>Rank 1K &rarr; <strong className="text-success">0.80</strong></span>
                <span>10K &rarr; <strong className="text-warning">0.73</strong></span>
                <span>50K &rarr; <strong className="text-orange-400">0.27</strong></span>
              </div>
            </div>

            {/* Traffic */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Traffic Signal
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                signal = sigmoid(traffic, 500)
              </code>
              <div className="flex gap-3 text-[11px] text-muted-foreground">
                <span>0 visits &rarr; <strong className="text-destructive">0.08</strong></span>
                <span>500 &rarr; <strong className="text-warning">0.50</strong></span>
                <span>5K &rarr; <strong className="text-success">0.99</strong></span>
              </div>
            </div>

            {/* Domain Ecosystem */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Domain Ecosystem
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                signal = sigmoid(referring_domains, 50)
              </code>
              <div className="flex gap-3 text-[11px] text-muted-foreground">
                <span>5 refs &rarr; <strong className="text-destructive">0.10</strong></span>
                <span>50 &rarr; <strong className="text-warning">0.50</strong></span>
                <span>200 &rarr; <strong className="text-success">0.95</strong></span>
              </div>
            </div>

            {/* Spam Risk */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-destructive" />
                Spam Risk <span className="text-[10px] text-destructive font-normal">(subtracted)</span>
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                spamRisk = max(moz_spam/17, dfs_spam/100)
              </code>
              <p className="text-[11px] text-muted-foreground">Takes worst of both sources. Moz 0-17, DFS 0-100.</p>
            </div>

            {/* Stability */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Stability
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                stability = (1 - failures/5) &times; min(days_stable/30, 1)
              </code>
              <div className="flex gap-3 text-[11px] text-muted-foreground">
                <span>0 fails, 30d &rarr; <strong className="text-success">1.0</strong></span>
                <span>5+ fails &rarr; <strong className="text-destructive">0.0</strong></span>
              </div>
            </div>

            {/* Diversity */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                IP &amp; Anchor Diversity
              </h4>
              <code className="block text-xs text-muted-foreground bg-muted/50 p-2 rounded font-mono">
                ipDiv = min(referring_ips / referring_domains, 1)<br/>
                anchorDiv = anchor_ratio || 0.5
              </code>
            </div>
          </div>

          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Sigmoid function:</strong>{' '}
              <code className="font-mono text-[11px]">sigmoid(x, mid) = 1 / (1 + e<sup>-0.05(x-mid)</sup>)</code>{' '}
              — smooth S-curve from 0 to 1 centered at the midpoint.
            </p>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* DATA SOURCES */}
      {/* ============================================ */}
      <FormulaSection
        icon={Database}
        iconColor="bg-emerald-500/10 text-emerald-500"
        title="Data Sources"
        subtitle="Where each metric comes from"
      >
        <div className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">
            Metrics come from two external APIs plus internal monitoring. Configure API keys on the{' '}
            <Link to="/integrations" className="text-primary hover:text-primary/80 underline">Integrations page</Link>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <h4 className="font-medium text-foreground text-sm">Moz API</h4>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Domain Authority (DA): 0-100</li>
                <li>Page Authority (PA): 0-100</li>
                <li>Spam Score: 0-17 scale (27 signals)</li>
              </ul>
              <p className="text-[11px] text-muted-foreground italic">Refreshed weekly or on-demand</p>
            </div>

            <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-emerald-500" />
                <h4 className="font-medium text-foreground text-sm">DataForSEO API</h4>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                <li>Domain Rank (lower = better)</li>
                <li>Total Backlinks count</li>
                <li>Referring Domains count</li>
                <li>Referring IPs count</li>
                <li>Spam Score: 0-100 scale</li>
                <li><strong>Est. Organic Traffic</strong> (Live mode only)</li>
              </ul>
              <p className="text-[11px] text-muted-foreground italic">On-demand. Live mode: $0.02/domain. Batch: $0.06 flat.</p>
            </div>
          </div>

          <div className="p-4 bg-muted/30 border border-border/50 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-foreground text-sm">Monitoring Data (Internal)</h4>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
              <li>Consecutive check failures</li>
              <li>Days since last status change</li>
              <li>Response time (ms)</li>
              <li>HTTP status, link type, anchor text</li>
            </ul>
            <p className="text-[11px] text-muted-foreground italic">Updated on every crawl check</p>
          </div>

          {/* DFS Mode explanation */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
            <h4 className="text-sm font-medium text-foreground">DFS API Modes</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-muted-foreground">
              <div className="p-2 bg-card rounded-lg border border-border/50">
                <p className="font-medium text-foreground mb-0.5">Live (Summary)</p>
                <p>1 API call per domain. Returns all data including backlinks count, referring IPs, and <strong>organic traffic</strong>. $0.02/domain.</p>
              </div>
              <div className="p-2 bg-card rounded-lg border border-border/50">
                <p className="font-medium text-foreground mb-0.5">Batch (3 Bulk calls)</p>
                <p>Rank + Spam + Refs in 3 calls. No backlinks count, IPs, or traffic. $0.06 flat for up to 1000 domains.</p>
              </div>
              <div className="p-2 bg-card rounded-lg border border-border/50">
                <p className="font-medium text-foreground mb-0.5">Auto (Default)</p>
                <p>1-2 domains: Live. 3+ domains: Batch. Best cost-efficiency for most workflows.</p>
              </div>
            </div>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* CHANGE DETECTION ALERTS */}
      {/* ============================================ */}
      <FormulaSection
        icon={Bell}
        iconColor="bg-red-500/10 text-red-500"
        title="Change Detection Alerts"
        subtitle="What changes are monitored and how alerts are triggered"
      >
        <div className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            During each crawl check, the system compares the current state of the backlink against stored values.
            When a change is detected, an alert is created. Duplicate alerts are suppressed for 24 hours.
          </p>

          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Link Removed / Status Changed</p>
                <p className="text-xs text-muted-foreground">Link was live but now returns 404, 5xx, or link element no longer found on page.</p>
              </div>
              <span className="text-xs font-mono text-destructive shrink-0 px-2 py-0.5 bg-destructive/10 rounded">link_removed</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-warning/5 border border-warning/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Anchor Text Changed</p>
                <p className="text-xs text-muted-foreground">The visible anchor text of the backlink was modified. Previous anchor is stored for comparison.</p>
              </div>
              <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">anchor_changed</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-warning/5 border border-warning/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Target URL Changed</p>
                <p className="text-xs text-muted-foreground">The href destination of the backlink was modified (e.g., redirected to a different page).</p>
              </div>
              <span className="text-xs font-mono text-warning shrink-0 px-2 py-0.5 bg-warning/10 rounded">target_url_changed</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-orange-400/5 border border-orange-400/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Link Type Changed</p>
                <p className="text-xs text-muted-foreground">Link rel attribute changed (e.g., dofollow &rarr; nofollow, or added ugc/sponsored).</p>
              </div>
              <span className="text-xs font-mono text-orange-400 shrink-0 px-2 py-0.5 bg-orange-400/10 rounded">link_type_changed</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-orange-400/5 border border-orange-400/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Link Position Changed</p>
                <p className="text-xs text-muted-foreground">Link moved from content area to sidebar, footer, or navigation (detected via DOM structure analysis).</p>
              </div>
              <span className="text-xs font-mono text-orange-400 shrink-0 px-2 py-0.5 bg-orange-400/10 rounded">link_position_changed</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Page Noindexed</p>
                <p className="text-xs text-muted-foreground">The linking page added a <code className="text-[11px] bg-muted px-1 rounded">noindex</code> meta robots tag, making the backlink invisible to search engines.</p>
              </div>
              <span className="text-xs font-mono text-destructive shrink-0 px-2 py-0.5 bg-destructive/10 rounded">page_noindex</span>
            </div>

            <div className="flex items-start gap-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <Activity className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">DA Drop</p>
                <p className="text-xs text-muted-foreground">Domain Authority dropped by 10+ points since last Moz refresh.</p>
              </div>
              <span className="text-xs font-mono text-blue-500 shrink-0 px-2 py-0.5 bg-blue-500/10 rounded">da_drop</span>
            </div>
          </div>

          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Deduplication:</strong> If an alert of the same type for the same backlink was already created in the last 24 hours, a new alert is not generated.
              This prevents alert floods during transient issues.
            </p>
          </div>

          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Link Evidence (stored per check):</strong> Each crawl captures a 200-char HTML snippet around the link, the link position (content/sidebar/footer/header), canonical URL, outbound link count, and the actual target URL found. View this evidence on the backlink detail page.
            </p>
          </div>
        </div>
      </FormulaSection>

      {/* ============================================ */}
      {/* PROVIDER SCORECARDS */}
      {/* ============================================ */}
      <FormulaSection
        icon={Users}
        iconColor="bg-indigo-500/10 text-indigo-500"
        title="Provider Scorecards"
        subtitle="How link agency/provider performance is measured"
      >
        <div className="space-y-6 mt-4">
          <p className="text-sm text-muted-foreground">
            Providers (link agencies, freelancers) can be assigned to backlinks during import or later via bulk actions.
            The scorecard aggregates metrics across all links from that provider.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm">Status Breakdown</h4>
              <p className="text-xs text-muted-foreground">
                % Live, % Lost (removed + 404 + 5xx), % Redirected, % Nofollow/UGC/Sponsored.
                A good provider keeps &gt;90% live.
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm">Quality Distribution</h4>
              <p className="text-xs text-muted-foreground">
                Count of Strong / Moderate / Weak / Toxic links. Median quality score across all provider links.
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm">Time-to-Loss</h4>
              <p className="text-xs text-muted-foreground">
                How quickly links are lost after placement, bucketed into 7d / 14d / 30d / 60d / 90d+.
                A pattern of early losses signals link quality issues.
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg space-y-2">
              <h4 className="font-medium text-foreground text-sm">Concentration Analysis</h4>
              <p className="text-xs text-muted-foreground">
                Top domains and anchors used. Flags if any single domain accounts for &gt;20% of links (over-reliance risk).
              </p>
            </div>
          </div>

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Batch Tracking</strong> &mdash; Each import can include a batch label and cost.
              The scorecard shows per-batch performance: count of links, cost, and % still live.
              Compare batches to identify which placements hold up over time.
            </p>
          </div>
        </div>
      </FormulaSection>
    </div>
  );
}
